import InputGroupContent from '@/components/elements/input'
import Icon from '@/components/shared/icons'
import { Box, Flex, Grid, Heading, Input, Text } from '@chakra-ui/react'
import Submit from '@/components/elements/submit'
import { useDispatch } from 'react-redux'
import { AppDispatch } from '@/store/store'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useEffect, useState } from 'react'
import Toast from '@/components/elements/toast'
import type { InferType } from 'yup'
import { useUploader } from '@/hooks/useUploader'
import SpinnerLoading from '@/components/elements/spinner'
import ImageItem from './parts/stock/image-item'
import useManualProgress from '@/hooks/useManualProgress'
import {
	customersRefrestUpdate,
	sessionRefrestUpdate,
} from '@/store/features/state'
import { useImageUploadToast } from '@/hooks/useImageUploadToast'
import CustomersEntity from '@/entities/api/customers/customers'
import { CustomerRoles } from '@/entities/api/roles'
import { createCustomer } from '@/services/api/customers/customers'
import { addCustomerFn } from '@/store/features/customers'
import { customerCreateSchema } from '@/entities/schemas/customer'
import { useFormErrorToast } from '@/hooks/useFormErrorToast'

type UserFormValues = InferType<typeof customerCreateSchema>

const AddCustomer = ({ close }: { close: () => void }) => {
	const {
		register,
		handleSubmit,
		formState: { errors },
	} = useForm<UserFormValues>({
		resolver: yupResolver(customerCreateSchema),
	})
	useFormErrorToast(errors)
	const dispatch = useDispatch<AppDispatch>()
	const {
		inputRef,
		files,
		isProcessing,
		handleFileChange,
		clearFiles,
		removeFile,
	} = useUploader()
	const [isUploadImage, setIsUploadImage] = useState<boolean>(false)
	const {
		progress,
		isLoading,
		startProgress,
		completeProgress,
		failProgress,
		wasFailed,
	} = useManualProgress()
	const { handleProgress } = useImageUploadToast()

	const onSubmit = async (dataYup: UserFormValues) => {
		try {
			startProgress()

			if (files.length < 1) {
				Toast({
					type: 'warning',
					message: 'Debes agregar la imagen del cliente',
				})
				await failProgress()
				return
			}

			const image: File[] = [files[0].file]
			const data: Partial<CustomersEntity> = {
				role: CustomerRoles.customer,
				name: dataYup.name,
				lastname: dataYup.lastname,
				email: dataYup.email,
				phone: dataYup.phone,
				password: dataYup.password,
			}

			const response = await createCustomer(data, image)

			if (response?.status === 409) {
				Toast({
					type: 'warning',
					message: 'Ya existe un cliente con ese numero o correo',
				})
				await failProgress()
				return
			}

			if (response?.status === 413) {
				Toast({
					type: 'error',
					message: 'El archivo es muy grande para ser procesado por el api',
				})
				await failProgress()
				return
			}

			if (response?.status === 401) {
				dispatch(sessionRefrestUpdate())
				await failProgress()
				return
			}

			dispatch(addCustomerFn(response?.data?.data ?? {}))
			dispatch(customersRefrestUpdate())
			await completeProgress()
			close()
			clearFiles()
		} catch (error) {
			console.log(error)
			Toast({ type: 'error', message: 'No se pudo crear el cliente' })
			await failProgress()
			close()
		}
	}

	useEffect(() => {
		if (files.length > 0) {
			setIsUploadImage(true)
			Toast({
				type: 'success',
				duration: 1000,
				message: 'La imagen se cargo correctamente',
			})
		}
		if (files.length >= 1) {
			Toast({
				type: 'info',
				duration: 1000,
				message: 'Solo puedes agregar 5 images por producto',
			})
		}
	}, [files])

	useEffect(() => {
		if (progress !== undefined && isLoading !== undefined) {
			handleProgress(progress, isLoading, wasFailed())
		}
	}, [progress, isLoading])

	return (
		<form onSubmit={handleSubmit(onSubmit)}>
			<Flex w='100%' h='full' flexDirection='column' alignItems='center'>
				<Box
					w='full'
					display='flex'
					justifyContent='start'
					alignItems='center'
					mb='2'
				>
					<Heading
						as='h4'
						fontSize={{ base: 'lg', md: 'xl' }}
						color='black'
						fontWeight='900'
					>
						Informacion general
					</Heading>
				</Box>
				<InputGroupContent
					label='Nombre del cliente'
					placeholder='Ingresa el nombre cliente'
					name='name'
					type='text'
					register={register}
					errors={errors}
				/>
				<InputGroupContent
					label='Apellidos del cliente'
					placeholder='Ingresa los apellidos del cliente'
					name='lastname'
					type='text'
					register={register}
					errors={errors}
				/>
				<InputGroupContent
					label='Correo'
					placeholder='Ingresa el correo'
					type='email'
					name='email'
					register={register}
					errors={errors}
				/>
				<InputGroupContent
					label='Numero telefonico'
					placeholder='Ingresa el numero telefonico'
					type='number'
					name='phone'
					register={register}
					errors={errors}
				/>
				<InputGroupContent
					label='Contraseña'
					placeholder='Ingresa la contraseña'
					type='password'
					name='password'
					register={register}
					errors={errors}
				/>

				<Box
					w='full'
					display='flex'
					justifyContent='start'
					alignItems='center'
					mb='4'
				>
					<Heading
						as='h4'
						fontSize={{ base: 'lg', md: 'xl' }}
						color='black'
						fontWeight='900'
					>
						Imagen (max 1)
					</Heading>
				</Box>
				<Box w='full' mb='4'>
					<Grid
						w='full'
						border='0.1px solid gray'
						borderRadius='lg'
						p='4'
						gap='4'
						templateColumns={{ base: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)' }}
					>
						{files &&
							isUploadImage &&
							files.map((item, index) => (
								<ImageItem
									key={index}
									id={item.id}
									name={item.name}
									preview={item.preview ?? ''}
									removeFile={removeFile}
								/>
							))}

						<Box position='relative' h='12vh'>
							{isProcessing ? (
								<SpinnerLoading />
							) : (
								<>
									<Input
										type='file'
										accept='image/*'
										multiple
										ref={inputRef}
										onChange={handleFileChange}
										opacity='0'
										position='absolute'
										h='full'
										w='full'
										disabled={files.length >= 1}
										cursor='pointer'
										zIndex='2'
									/>
									<Box
										h='full'
										w='full'
										border='2px dashed gray'
										display='flex'
										alignItems='center'
										justifyContent='center'
										borderRadius='md'
										position='relative'
										zIndex='1'
										p='2'
										bg='gray.100'
										_hover={{ bg: 'gray.200' }}
									>
										<Icon
											icon={files.length >= 1 ? 'disabled' : 'camera'}
											size='xl'
											color='gray'
										/>
										<Text
											ml='2'
											color='gray.500'
											fontSize='sm'
											display={{ base: 'none', md: 'block' }}
										>
											{files.length >= 1 ? 'Candidad Max' : 'Añadir Imagen'}
										</Text>
									</Box>
								</>
							)}
						</Box>
					</Grid>
				</Box>
				<Box pt='4' pb='8'>
					{isLoading ? (
						<SpinnerLoading color='black' />
					) : (
						<Submit text='Agregar cliente' />
					)}
				</Box>
			</Flex>
		</form>
	)
}

export default AddCustomer
